import { Role, hasRole } from '../roles'
import { VulnerabilityStatus } from '../../graphql/schema/shared'

export class SubstatusRegistry {
  constructor(readonly all: Substatus[]) {}

  public fromName = (name: string): Substatus => {
    const substatus = this.all.find(
      (substatus) => substatus.name.toUpperCase() === name.toUpperCase()
    )

    if (!substatus) {
      throw new Error(`Unknown substatus: ${name}`)
    }

    return substatus
  }
}

const emptySubstatusRegistry = new SubstatusRegistry([])

export type SubstatusName = 'URL' | 'FP' | 'SC' | 'EC' | 'OT'

export class Substatus {
  public static readonly TRUSTED: Substatus = new Substatus(
    'URL',
    'detail_vulnerabilitySubstatus_trustedURL',
    'detail_vulnerabilitySubstatus_trustedURL_short',
    false
  )
  public static readonly FALSE_POSITIVE: Substatus = new Substatus(
    'FP',
    'detail_vulnerabilitySubstatus_falsePositive',
    'detail_vulnerabilitySubstatus_falsePositive',
    false
  )
  public static readonly INTERNAL_SECURITY_CONTROL: Substatus = new Substatus(
    'SC',
    'detail_vulnerabilitySubstatus_internalControl',
    'detail_vulnerabilitySubstatus_internalControl_short',
    false
  )
  public static readonly EXTERNAL_SECURITY_CONTROL: Substatus = new Substatus(
    'EC',
    'detail_vulnerabilitySubstatus_externalControl',
    'detail_vulnerabilitySubstatus_externalControl_short',
    false
  )
  public static readonly OTHER: Substatus = new Substatus(
    'OT',
    'detail_vulnerabilitySubstatus_other',
    'detail_vulnerabilitySubstatus_other',
    true
  )

  constructor(
    readonly name: SubstatusName,
    readonly i18nID: string,
    readonly i18nIDShort: string,
    readonly requiresJustification: boolean,
    readonly alias?: string
  ) {}
}

export enum Classification {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED'
}

export enum Source {
  MANUAL = 'MANUAL',
  AUTO = 'AUTO'
}

export function getDefaultSubstatusRegistry() {
  return new SubstatusRegistry([
    Substatus.TRUSTED,
    Substatus.FALSE_POSITIVE,
    Substatus.INTERNAL_SECURITY_CONTROL,
    Substatus.EXTERNAL_SECURITY_CONTROL,
    Substatus.OTHER
  ])
}

export class StatusRegistry {
  public readonly all: Status[]
  public readonly open: Status[]
  public readonly closed: Status[]
  public readonly manual: Status[]
  public readonly openManual: Status[]
  public readonly closedManual: Status[]

  constructor(readonly statuses: Status[]) {
    this.all = statuses
    this.open = statuses.filter(
      (status) => status.classificiation === Classification.OPEN
    )
    this.closed = statuses.filter(
      (status) => status.classificiation === Classification.CLOSED
    )
    this.manual = statuses.filter((status) => status.source === Source.MANUAL)
    this.openManual = statuses.filter(
      (status) =>
        status.classificiation === Classification.OPEN &&
        status.source === Source.MANUAL
    )
    this.closedManual = statuses.filter(
      (status) =>
        status.classificiation === Classification.CLOSED &&
        status.source === Source.MANUAL
    )
  }

  public fromName = (name: string): Status => {
    const status = this.all.find((status) => status.name === name)

    if (!status) {
      throw new Error(`Unknown status: ${name}`)
    }

    return status
  }
}

export class Status {
  public static readonly REPORTED: Status = new Status(
    'Reported',
    'title_vulnerabilityStatusReported',
    Classification.OPEN,
    Source.MANUAL,
    emptySubstatusRegistry,
    'EDIT'
  )
  public static readonly SUSPICIOUS: Status = new Status(
    'Suspicious',
    'title_vulnerabilityStatusSuspicious',
    Classification.OPEN,
    Source.MANUAL,
    emptySubstatusRegistry,
    'EDIT'
  )
  public static readonly CONFIRMED: Status = new Status(
    'Confirmed',
    'title_vulnerabilityStatusConfirmed',
    Classification.OPEN,
    Source.MANUAL,
    emptySubstatusRegistry,
    'EDIT'
  )
  public static readonly NOT_A_PROBLEM: Status = new Status(
    'NotAProblem',
    'title_vulnerabilityStatusNotProblem',
    Classification.CLOSED,
    Source.MANUAL,
    getDefaultSubstatusRegistry(),
    'EDIT'
  )
  public static readonly REMEDIATED: Status = new Status(
    'Remediated',
    'title_vulnerabilityStatusRemediated',
    Classification.CLOSED,
    Source.MANUAL,
    emptySubstatusRegistry,
    'EDIT'
  )
  public static readonly FIXED: Status = new Status(
    'Fixed',
    'title_vulnerabilityStatusFixed',
    Classification.CLOSED,
    Source.MANUAL,
    emptySubstatusRegistry,
    'RULES_ADMIN'
  )
  public static readonly AUTO_REMEDIATED: Status = new Status(
    'AutoRemediated',
    'title_vulnerabilityStatusAutoRemediated',
    Classification.CLOSED,
    Source.AUTO,
    emptySubstatusRegistry
  )

  public static readonly all: Status[] = [
    Status.REPORTED,
    Status.SUSPICIOUS,
    Status.CONFIRMED,
    Status.NOT_A_PROBLEM,
    Status.REMEDIATED,
    Status.FIXED,
    Status.AUTO_REMEDIATED
  ]

  public static fromName = (name: VulnerabilityStatus): Status => {
    const status = Status.all.find((status) => status.name === name)

    if (!status) {
      throw new Error(`Unknown status: ${name}`)
    }

    return status
  }

  constructor(
    readonly name: string,
    readonly i18nID: string,
    readonly classificiation: Classification,
    readonly source: Source,
    readonly substatusRegistry: SubstatusRegistry,
    readonly requiredRole?: Role
  ) {}

  public isAllowedByRole = (role: Role) => {
    return this.requiredRole && hasRole(role, this.requiredRole)
  }

  public requiresReason = () => {
    return this.substatusRegistry.all.length > 0
  }

  public requiresJustification = (substatus?: Substatus) => {
    return !!(substatus && substatus.requiresJustification)
  }
}

export const defaultStatusRegistry = new StatusRegistry([
  Status.REPORTED,
  Status.SUSPICIOUS,
  Status.CONFIRMED,
  Status.NOT_A_PROBLEM,
  Status.REMEDIATED,
  Status.FIXED,
  Status.AUTO_REMEDIATED
])
